import { useEffect, useRef, useState, type ReactNode } from 'react';
import { extractApiErrorMessage } from '../../utils/apiError';

interface TimelineNotesFieldProps {
  label: string;
  icon?: ReactNode;
  value: string | null;
  /** Saves the text ('' clears it). */
  onSave: (value: string) => Promise<unknown>;
  placeholder?: string;
  readOnly?: boolean;
  maxLength?: number;
  testId?: string;
}

type Status = 'idle' | 'saving' | 'saved' | 'error';

/**
 * A notes card (the rain plan) that saves once, when the field is left —
 * never on every keystroke, which sent a request per letter and let a
 * refetch land mid-word and swallow what was being typed. The stored text is
 * taken only while nothing unsaved is in the field. Every keystroke is counted,
 * and a save marks the field clean only up to the keystroke it carried — text
 * typed while it was on its way stays unsaved, and is never replaced by a
 * refetch. Saves go one at a time, so an older one never lands after a newer.
 */
export function TimelineNotesField({
  label,
  icon,
  value,
  onSave,
  placeholder,
  readOnly = false,
  maxLength = 2000,
  testId,
}: TimelineNotesFieldProps) {
  const stored = value ?? '';
  const [draft, setDraft] = useState(stored);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  // Keystrokes typed, and the keystroke the last finished save carried.
  const edits = useRef(0);
  const savedAt = useRef(0);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const isDirty = () => edits.current !== savedAt.current;
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    if (!isDirty()) setDraft(stored);
  }, [stored]);

  const save = () => {
    if (!isDirty()) return;
    const at = edits.current;
    const next = draftRef.current;
    if (next === stored) {
      savedAt.current = at;
      return;
    }
    setStatus('saving');
    queue.current = queue.current.then(async () => {
      try {
        await onSave(next);
        savedAt.current = Math.max(savedAt.current, at);
        setError('');
        // Typed on while it went: that text still waits for the next save.
        setStatus(isDirty() ? 'idle' : 'saved');
      } catch (err) {
        setStatus('error');
        setError(extractApiErrorMessage(err, 'Could not save the notes — try again'));
      }
    });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
          {icon}
          {label}
        </span>
        <span className="text-xs text-gray-400" aria-live="polite">
          {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : ''}
        </span>
      </div>
      {readOnly ? (
        <p className="text-sm text-gray-700 whitespace-pre-line" data-testid={testId}>
          {stored || <span className="text-gray-400">None</span>}
        </p>
      ) : (
        <>
          <textarea
            aria-label={label}
            value={draft}
            onChange={(e) => {
              edits.current += 1;
              setDraft(e.target.value);
              if (status !== 'idle') setStatus('idle');
            }}
            onBlur={save}
            placeholder={placeholder}
            maxLength={maxLength}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary-500"
            data-testid={testId}
          />
          {status === 'error' && (
            <p role="alert" className="mt-1 text-xs text-red-700">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
